import { useState, useEffect, useRef } from 'react';
import './CallSimulator.css';
import GeminiService from '../services/GeminiService';
import VoIPService from '../services/VoIPService';

const CallSimulator = ({ userConfig, callData, onCallComplete }) => {
  const [callStage, setCallStage] = useState('initializing');
  const [transcript, setTranscript] = useState([]);
  const [isRecording, setIsRecording] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [delegateNumber, setDelegateNumber] = useState(null);
  const callInitialized = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Prevent duplicate initialization
    if (callInitialized.current) {
      console.log('⚠️ CallSimulator useEffect called again - preventing duplicate');
      return;
    }
    
    // Clear any existing interval
    if (intervalRef.current) {
      console.log('🧹 Clearing existing interval');
      clearInterval(intervalRef.current);
    }
    
    callInitialized.current = true;
    console.log('🚀 Initializing CallSimulator for the first time');
    // Initialize services
    const aiConfigured = GeminiService.setApiKey(userConfig.apiKey);
    console.log('🔑 API Key configured:', userConfig.apiKey ? 'Yes' : 'No');
    console.log('🤖 AI Service ready:', aiConfigured ? 'Yes' : 'No');
    
    intervalRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    const addToTranscriptWithVoIP = (speaker, message) => {
      const timestamp = formatTime(elapsedTime);
      const newEntry = { speaker, message, timestamp };
      
      console.log(`📝 Adding to transcript: ${speaker}: ${message.substring(0, 50)}...`);
      
      setTranscript(prev => {
        // Check if this same message was added recently (last 3 entries or same message)
        const recentDuplicate = prev.slice(-3).some(entry => 
          entry.speaker === speaker && 
          entry.message === message
        );
        
        if (recentDuplicate) {
          console.warn('⚠️ Duplicate message detected, skipping:', speaker, message.substring(0, 30));
          return prev;
        }
        
        console.log(`✅ Adding new entry: ${speaker} at ${timestamp}`);
        return [...prev, newEntry];
      });
      
      // Add VoIP packet information (safely handle undefined)
      try {
        const packetInfo = VoIPService.addVoicePacket(speaker, message, timestamp);
        if (packetInfo && packetInfo.packetId) {
          console.log(`VoIP Packet: ${packetInfo.packetId} - Latency: ${packetInfo.latency}ms`);
        }
      } catch (error) {
        console.warn('VoIP packet logging failed:', error);
      }
    };

    const generateBusinessResponse = async (message) => {
      try {
        console.log('🏢 Using AI to generate business response for:', callData.businessName);
        const aiResponse = await GeminiService.generateBusinessResponse(message, {
          businessName: callData.businessName,
          serviceType: callData.serviceType,
          projectDescription: callData.projectDescription
        });
        console.log('✅ Business AI Response received:', aiResponse.substring(0, 100) + '...');
        return aiResponse;
      } catch (error) {
        console.error('❌ Error generating business response:', error);
        // Only fallback if absolutely necessary - still try to be AI-powered
        return `Thank you for calling ${callData.businessName || 'our business'}. We'd be happy to help with your ${callData.serviceType || 'service'} needs. Could you tell me more about what you're looking for?`;
      }
    };

    const generateAIResponse = async (lastMessage, conversationHistory, callData, isClosing = false) => {
      try {
        console.log('🤖 Using AI to generate AI assistant response');
        
        let systemPrompt;
        if (isClosing) {
          systemPrompt = `You are an AI assistant making a business call on behalf of ${userConfig.name}. 
          
          CONTEXT: You called ${callData.businessName} requesting ${callData.serviceType} services.
          PROJECT: ${callData.projectDescription}
          
          The business has provided information/quotes. Now wrap up the call professionally:
          - Thank them for their time and information
          - Confirm next steps or follow-up process  
          - Provide your client's contact info: ${userConfig.phone} and ${userConfig.email}
          - Keep it brief and professional
          
          Respond naturally to their last message: "${lastMessage}"`;
        } else {
          systemPrompt = `You are an AI assistant making a business call on behalf of ${userConfig.name}.
          
          CONTEXT: You're calling ${callData.businessName} about ${callData.serviceType} services.
          PROJECT DETAILS: ${callData.projectDescription}
          CLIENT CONTACT: ${userConfig.phone}, ${userConfig.email}
          
          INSTRUCTIONS:
          - Be professional and conversational
          - Ask relevant questions about pricing, timeline, process
          - Provide project details when asked
          - Sound like a helpful assistant, not a robot
          - Keep responses conversational and under 2-3 sentences
          - Respond naturally to their last message: "${lastMessage}"
          
          Do not repeat information already discussed in the conversation.`;
        }

        // Create a prompt that includes the system instruction and conversation context
        const fullPrompt = `${systemPrompt}

CONVERSATION SO FAR:
${conversationHistory.map(msg => `${msg.role === 'assistant' ? 'AI Assistant' : 'Business Rep'}: ${msg.content}`).join('\n')}

Now respond naturally to their message: "${lastMessage}"

Your response:`;

        const response = await GeminiService.generateResponse(fullPrompt, []);
        
        console.log('✅ AI Assistant Response received:', response.substring(0, 100) + '...');
        return response;
      } catch (error) {
        console.error('❌ Error generating AI assistant response:', error);
        // Only minimal fallback if AI completely fails
        if (isClosing) {
          return `Thank you for your time! ${userConfig.name} can be reached at ${userConfig.phone} or ${userConfig.email}.`;
        } else {
          return `I'm calling about ${callData.serviceType} services for ${userConfig.name}.`;
        }
      }
    };

    const extractQuoteFromTranscript = async (transcript) => {
      try {
        console.log('🔍 Extracting quote information from transcript using AI...');
        
        const transcriptText = transcript.map(entry => 
          `${entry.speaker}: ${entry.message}`
        ).join('\n');

        const extractionPrompt = `Analyze this business call transcript and extract the quote/pricing information that was actually discussed. Return a JSON object with these fields:

estimateRange: The price range or specific quote mentioned (e.g., "$3,000-5,000" or "Quote provided")
estimateFee: Cost for estimate/consultation (e.g., "Free estimate" or "$50 consultation fee")
appointmentScheduled: What was arranged for next steps (e.g., "Site visit scheduled for Thursday" or "Will call back tomorrow")
followUpMethod: How they will follow up (e.g., "Phone call" or "Email quote" or "In-person estimate")

TRANSCRIPT:
${transcriptText}

Extract only what was actually mentioned in the conversation. If something wasn't discussed, use "Not discussed" as the value.

Return only valid JSON:`;

        const response = await GeminiService.generateResponse(extractionPrompt, []);
        
        // Try to parse the JSON response
        const cleanResponse = response.replace(/```json\n?|\n?```/g, '').trim();
        const extractedData = JSON.parse(cleanResponse);
        
        console.log('✅ Quote extraction successful:', extractedData);
        return {
          estimateRange: extractedData.estimateRange || 'Quote discussed in call',
          estimateFee: extractedData.estimateFee || 'See transcript for details',
          appointmentScheduled: extractedData.appointmentScheduled || 'Next steps discussed',
          followUpMethod: extractedData.followUpMethod || 'As arranged in call',
          outcome: `Quote discussion completed - ${extractedData.estimateRange || 'pricing discussed'}`
        };
        
      } catch (error) {
        console.error('❌ Error extracting quote from transcript:', error);
        return {
          estimateRange: 'Quote discussed in call',
          estimateFee: 'See transcript for details', 
          appointmentScheduled: 'Next steps discussed',
          followUpMethod: 'As arranged in call',
          outcome: 'Quote discussion completed via AI-powered call'
        };
      }
    };

    const beginAIConversation = async () => {
      try {
        setCallStage('talking');
        const conversationHistory = [];
        
        // AI opens with required statement
        const openingMessage = `This is an AI voice call on behalf of ${userConfig.name}. To stop transcriptions from being recorded, please press 1.`;
        addToTranscriptWithVoIP('AI Assistant', openingMessage);
        conversationHistory.push({role: 'assistant', content: openingMessage});
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Business responds to AI opening
        const businessGreeting = await generateBusinessResponse(openingMessage);
        addToTranscriptWithVoIP('Business Rep', businessGreeting);
        conversationHistory.push({role: 'user', content: businessGreeting});
        
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // AI explains the purpose naturally based on business greeting
        const aiResponse1 = await generateAIResponse(businessGreeting, conversationHistory, callData);
        addToTranscriptWithVoIP('AI Assistant', aiResponse1);
        conversationHistory.push({role: 'assistant', content: aiResponse1});
        
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Business responds to AI's explanation
        const businessResponse1 = await generateBusinessResponse(aiResponse1);
        addToTranscriptWithVoIP('Business Rep', businessResponse1);
        conversationHistory.push({role: 'user', content: businessResponse1});
        
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // AI follows up naturally based on business response
        const aiResponse2 = await generateAIResponse(businessResponse1, conversationHistory, callData);
        addToTranscriptWithVoIP('AI Assistant', aiResponse2);
        conversationHistory.push({role: 'assistant', content: aiResponse2});
        
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Business provides quote/estimate
        const businessQuote = await generateBusinessResponse(aiResponse2);
        addToTranscriptWithVoIP('Business Rep', businessQuote);
        conversationHistory.push({role: 'user', content: businessQuote});
        
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // AI wraps up naturally
        const aiClosing = await generateAIResponse(businessQuote, conversationHistory, callData, true);
        addToTranscriptWithVoIP('AI Assistant', aiClosing);
        conversationHistory.push({role: 'assistant', content: aiClosing});
        
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Business closing
        const businessClosing = await generateBusinessResponse(aiClosing);
        addToTranscriptWithVoIP('Business Rep', businessClosing);
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        setCallStage('completed');
        setTimeout(async () => {
          // Get current transcript and extract quote information
          setTranscript(currentTranscript => {
            // Use the transcript to extract quote info asynchronously
            extractQuoteFromTranscript(currentTranscript).then(extractedQuote => {
              const callResult = {
                status: 'completed',
                transcript: currentTranscript,
                outcome: extractedQuote.outcome || 'Quote discussion completed via AI-powered call',
                quote: extractedQuote
              };
              
              console.log('🔍 Completing call with extracted quote:', {
                transcriptLength: currentTranscript.length,
                extractedQuote: extractedQuote
              });
              
              onCallComplete(callResult);
            }).catch(error => {
              console.error('Error extracting quote:', error);
              // Fallback to default quote if extraction fails
              const callResult = {
                status: 'completed',
                transcript: currentTranscript,
                outcome: 'Quote discussion completed via AI-powered call',
                quote: {
                  estimateRange: 'Quote discussed in call',
                  estimateFee: 'See transcript for details',
                  appointmentScheduled: 'Next steps discussed',
                  followUpMethod: 'As arranged in call'
                }
              };
              onCallComplete(callResult);
            });
            
            return currentTranscript;
          });
        }, 1500);

      } catch (error) {
        console.error('Error during AI conversation:', error);
        // Fallback simulation
        addToTranscriptWithVoIP('Business Rep', 'Hello, thank you for calling! How can I help you today?');
        setTimeout(() => addToTranscriptWithVoIP('AI Assistant', `I'm calling about ${callData.serviceType} services.`), 2000);
        setTimeout(() => addToTranscriptWithVoIP('Business Rep', 'Great! We can provide a quote for that.'), 4000);
        setTimeout(() => {
          setCallStage('completed');
          setTimeout(() => {
            setTranscript(currentTranscript => {
              // Extract quote information from fallback transcript too
              extractQuoteFromTranscript(currentTranscript).then(extractedQuote => {
                const callResult = {
                  status: 'completed',
                  transcript: currentTranscript,
                  outcome: extractedQuote.outcome || 'Quote discussion completed via VoIP call',
                  quote: extractedQuote
                };
                
                console.log('🔍 Fallback completing call with extracted quote:', {
                  transcriptLength: currentTranscript.length,
                  extractedQuote: extractedQuote
                });
                
                onCallComplete(callResult);
              }).catch(error => {
                console.error('Error extracting quote in fallback:', error);
                const callResult = {
                  status: 'completed',
                  transcript: currentTranscript,
                  outcome: 'Quote discussion completed via VoIP call',
                  quote: {
                    estimateRange: 'Quote discussed in call',
                    estimateFee: 'See transcript for details',
                    appointmentScheduled: 'Next steps discussed',
                    followUpMethod: 'As arranged in call'
                  }
                };
                onCallComplete(callResult);
              });
              
              return currentTranscript;
            });
          }, 1000);
        }, 6000);
      }
    };

    // Simulate VoIP connection stages and initialize call session
    setCallStage('initializing');
    const numberInfo = VoIPService.assignDelegateNumber(userConfig);
    setDelegateNumber(numberInfo.number);
    
    // Initialize a simple call session for VoIP packet tracking
    VoIPService.currentCall = {
      id: `call_${Date.now()}`,
      delegateNumber: numberInfo.number,
      targetNumber: callData.businessPhone,
      userConfig,
      callData,
      startTime: new Date(),
      status: 'connected',
      voicePackets: []
    };
    
    setTimeout(() => setCallStage('voice_to_data'), 1000);
    setTimeout(() => setCallStage('internet_transmission'), 2000);
    setTimeout(() => setCallStage('pstn_connection'), 3000);
    setTimeout(() => setCallStage('caller_id_setup'), 4000);
    setTimeout(() => setCallStage('connected'), 5000);
    setTimeout(() => beginAIConversation(), 6000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      callInitialized.current = false; // Reset for next mount
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper functions
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };


  const addToTranscript = (speaker, message) => {
    const timestamp = formatTime(elapsedTime);
    const newEntry = { speaker, message, timestamp };
    setTranscript(prev => [...prev, newEntry]);
  };


  const toggleRecording = () => {
    setIsRecording(!isRecording);
    addToTranscript('System', isRecording ? 'Recording stopped by business request' : 'Recording resumed');
  };

  const getStageDisplay = () => {
    switch (callStage) {
      case 'initializing':
        return { text: 'Initializing VoIP...', color: '#8b5cf6' };
      case 'voice_to_data':
        return { text: 'Converting Voice to Data', color: '#f59e0b' };
      case 'internet_transmission':
        return { text: 'Routing via Internet', color: '#06b6d4' };
      case 'pstn_connection':
        return { text: 'Connecting to Phone Network', color: '#f97316' };
      case 'caller_id_setup':
        return { text: 'Setting Caller ID', color: '#84cc16' };
      case 'connected':
        return { text: 'VoIP Connected', color: '#10b981' };
      case 'talking':
        return { text: 'AI Conversation Active', color: '#3b82f6' };
      case 'completed':
        return { text: 'Call Completed', color: '#10b981' };
      case 'error':
        return { text: 'Connection Error', color: '#ef4444' };
      default:
        return { text: 'Processing...', color: '#6b7280' };
    }
  };

  const stageDisplay = getStageDisplay();

  return (
    <div className="call-simulator">
      <div className="call-header">
        <h3>AI Call in Progress</h3>
        <div className="call-info">
          <div className="call-status">
            <span className="status-dot" style={{ backgroundColor: stageDisplay.color }}></span>
            {stageDisplay.text}
          </div>
          <div className="call-timer">
            Duration: {formatTime(elapsedTime)}
          </div>
        </div>
      </div>

      <div className="call-details">
        <div className="calling-info">
          <p><strong>Calling:</strong> {callData.businessName}</p>
          <p><strong>Target Phone:</strong> {callData.businessPhone}</p>
          {delegateNumber && (
            <p><strong>Delegate Number:</strong> {delegateNumber}</p>
          )}
          <p><strong>Service:</strong> {callData.serviceType}</p>
        </div>
        
        <div className="recording-controls">
          <div className={`recording-indicator ${isRecording ? 'recording' : 'paused'}`}>
            <span className="recording-dot"></span>
            {isRecording ? 'Recording' : 'Recording Paused'}
          </div>
          <button 
            className="btn btn-small"
            onClick={toggleRecording}
          >
            {isRecording ? 'Pause Recording' : 'Resume Recording'}
          </button>
        </div>
      </div>


      <div className="transcript-container">
        <h4>Live Transcript</h4>
        <div className="transcript">
          {transcript.map((entry, index) => (
            <div key={index} className={`transcript-entry ${entry.speaker.toLowerCase().replace(' ', '-')}`}>
              <div className="speaker-info">
                <span className="speaker-name">{entry.speaker}</span>
                <span className="timestamp">{entry.timestamp}</span>
              </div>
              <div className="message">{entry.message}</div>
            </div>
          ))}
          {transcript.length === 0 && (
            <div className="transcript-placeholder">
              Transcript will appear here once the call begins...
            </div>
          )}
        </div>
      </div>

      {callStage === 'completed' && (
        <div className="call-summary">
          <h4>Call Summary</h4>
          <p>✅ Successfully connected and discussed project requirements</p>
          <p>✅ Obtained estimate range and fee information</p>
          <p>✅ Scheduled follow-up appointment</p>
        </div>
      )}
    </div>
  );
};

export default CallSimulator;